import api from './api';

// Below this size the blob is just a container header — the mic produced no audio
// (muted system mic, another app holding the device, a track that ended early).
export const MIN_AUDIO_BYTES = 1500;
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg', ''];

export function pickMime() {
  return MIME_CANDIDATES.find(m => !m || window.MediaRecorder?.isTypeSupported?.(m)) || '';
}

export function fileNameFor(mime) {
  if (/mp4/.test(mime)) return 'recording.m4a';
  if (/ogg/.test(mime)) return 'recording.ogg';
  return 'recording.webm';
}

/** MediaRecorder with a 1 s timeslice — data is flushed every second, so a hiccup at
 *  stop() (Safari track-ended, backgrounded tab) can't lose the whole take. */
export function startRecorder(stream, onBlob) {
  const mime = pickMime();
  const r = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
  const chunks = [];
  r.ondataavailable = e => { if (e.data && e.data.size > 0) chunks.push(e.data); };
  r.onstop = () => onBlob(new Blob(chunks, { type: mime || r.mimeType || 'audio/webm' }));
  r.start(1000);
  return r;
}

export function isEmptyRecording(blob) {
  return !blob || blob.size < MIN_AUDIO_BYTES;
}

export function uploadErrorMessage(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (status === 413) return 'Recording is too large. Please keep responses under 20 MB.';
  if (status === 415) return 'Unsupported audio format. Please retry.';
  if (status === 401) return 'Session expired. Please log in and retry.';
  if (status === 503 && typeof detail === 'string') return detail;
  if (err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')) return 'The upload timed out — check your connection and retry.';
  if (/Network/i.test(err?.message || '')) return 'Network error — check your connection and retry.';
  return 'Upload failed — please retry.';
}

/** Upload with automatic retries on network errors / 5xx (never on 4xx). */
export async function postAudio(url, formData, { attempts = 3, timeout = 120000 } = {}) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      return await api.post(url, formData, { timeout });
    } catch (err) {
      lastErr = err;
      const s = err?.response?.status;
      if (s && s < 500 && s !== 408 && s !== 429) throw err;
      if (i < attempts - 1) await new Promise(res => setTimeout(res, 1500 * (i + 1)));
    }
  }
  throw lastErr;
}
